// Écran « Collecteurs » (administration seulement) : les collecteurs sur site qui déposent les lectures signées.
// Liste (nom, état, demande, dernière activité, clé à renouveler, sources liées) puis fiche (CollecteurFiche.tsx).
// Règles : jamais de suppression ; l'API ne rend ni clé, ni kid, ni empreinte d'une demande en attente (O63) et l'écran
// n'en montre donc aucune ; rien n'est écrit dans le stockage du navigateur, l'adresse ou la console.
import { useQuery } from '@tanstack/react-query';
import type { CollecteurVue } from '../api/types';
import { Attendre, Etiquette, EtiquetteEtatCollecteur } from '../composants/communs';
import { useEkera } from '../contexte';
import { EXPLICATION_ETAT_COLLECTEUR, nomCollecteur, SANS_CLE_VISIBLE } from '../domaine/collecteurs';
import { dateFr } from '../domaine/format';
import { lienVers } from '../routeur';
import { FicheCollecteur } from './CollecteurFiche';

export function EcranCollecteurs({ params }: { params: URLSearchParams }) {
  const { administrateur } = useEkera();
  if (!administrateur)
    return (
      <section className="panneau" aria-labelledby="t-reserve">
        <h2 id="t-reserve">Réservé à l’administration</h2>
        <p>
          Les collecteurs sur site, leur enrôlement et leur révocation ne sont ouverts qu’aux personnes qui ont le rôle
          « administration ». Voyez votre administrateur.
        </p>
      </section>
    );
  const id = params.get('collecteur');
  return <div className="pile">{id ? <FicheCollecteur id={id} /> : <ListeCollecteurs />}</div>;
}

function ListeCollecteurs() {
  const { client } = useEkera();
  const liste = useQuery({ queryKey: ['collecteurs'], queryFn: () => client.collecteurs() });
  return (
    <>
      <p className="sous-titre">
        Un collecteur est le programme installé chez vous qui lit vos logiciels et dépose ses lectures, signées par sa propre
        clé. Son enrôlement est approuvé par <b>deux administrateurs distincts</b> (quatre yeux) ; une révocation est définitive.
      </p>
      <Attendre etats={[liste]} quoi="des collecteurs">
        {() => <TableauCollecteurs collecteurs={liste.data ?? []} />}
      </Attendre>
    </>
  );
}

function TableauCollecteurs({ collecteurs }: { collecteurs: CollecteurVue[] }) {
  if (collecteurs.length === 0)
    return (
      <section className="panneau" aria-labelledby="t-vide">
        <h2 id="t-vide">Aucun collecteur enrôlé</h2>
        <p>
          Un collecteur demande son enrôlement depuis son propre serveur ; la demande apparaît ici, et deux administrateurs
          doivent l’approuver.
        </p>
      </section>
    );
  const attente = collecteurs.filter((c) => c.etat === 'en_attente').length;
  const renouveler = collecteurs.filter((c) => c.cle_a_renouveler).length;
  return (
    <>
      {attente > 0 && (
        <div className="encadre attention" role="status">
          <p>
            <b>{attente === 1 ? '1 demande attend' : `${attente} demandes attendent`} une approbation.</b> Ouvrez la fiche, lisez
            l’empreinte sur le serveur du collecteur, puis saisissez-la.
          </p>
        </div>
      )}
      {renouveler > 0 && (
        <div className="encadre info" role="status">
          <p>
            {renouveler === 1 ? '1 clé est active' : `${renouveler} clés sont actives`} depuis plus de 12 mois : le collecteur
            peut la renouveler (aucune rotation n’est imposée).
          </p>
        </div>
      )}
      <section className="panneau" aria-labelledby="t-collecteurs">
        <h2 id="t-collecteurs">Collecteurs</h2>
        <div className="tableau-defile">
          <table>
            <caption className="sr">Collecteurs sur site, avec leur état et leur dernière activité</caption>
            <thead>
              <tr>
                <th scope="col">Collecteur</th>
                <th scope="col">État</th>
                <th scope="col">Demandé le</th>
                <th scope="col">Dernière activité</th>
                <th scope="col">Clé</th>
                <th scope="col" className="num">
                  Sources liées
                </th>
              </tr>
            </thead>
            <tbody>
              {collecteurs.map((c) => (
                <tr key={c.id}>
                  <th scope="row">
                    <a href={lienVers('collecteurs', { collecteur: c.id })}>{nomCollecteur(c)}</a>
                    <span className="note" style={{ display: 'block', fontWeight: 400 }}>
                      {c.id}
                    </span>
                  </th>
                  <td>
                    <EtiquetteEtatCollecteur etat={c.etat} />
                    <span className="note" style={{ display: 'block' }}>
                      {EXPLICATION_ETAT_COLLECTEUR[c.etat]}
                    </span>
                  </td>
                  <td>{dateFr(c.cree_le)}</td>
                  <td>{c.derniere_activite ? dateFr(c.derniere_activite) : <span className="note">Aucune</span>}</td>
                  <td>
                    {SANS_CLE_VISIBLE.includes(c.etat) || !c.cle_depuis ? (
                      <span className="note">—</span>
                    ) : c.cle_a_renouveler ? (
                      <Etiquette ton="attention">À renouveler</Etiquette>
                    ) : (
                      <span className="note">Active depuis le {dateFr(c.cle_depuis)}</span>
                    )}
                  </td>
                  <td className="num">{c.sources}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="note">
          Aucun collecteur n’est jamais supprimé : un collecteur refusé ou révoqué reste listé, avec son journal de sécurité.
        </p>
      </section>
    </>
  );
}
